import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-colors";

// Type-ahead picker restricted to the given options (no free text)
export function Combobox({ value, onChange, options, placeholder = "Select…", testid, className, inputClassName }) {
  const opts = useMemo(() => (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o)), [options]);
  const current = opts.find((o) => o.value === value);
  const shownText = (o) => (o ? o.display || o.label : "");
  const [text, setText] = useState(shownText(current));
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const listRef = useRef();
  useEffect(() => { if (!open) setText(shownText(current)); }, [current, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = text.trim().toLowerCase();
  const filtered = !q || q === shownText(current).toLowerCase() ? opts
    : [...opts.filter((o) => o.label.toLowerCase().startsWith(q) || o.value.toLowerCase() === q),
       ...opts.filter((o) => !o.label.toLowerCase().startsWith(q) && o.value.toLowerCase() !== q && o.label.toLowerCase().includes(q))];

  const pick = (o) => { setOpen(false); setText(shownText(o)); if (o.value !== value) onChange(o.value); };
  const commit = () => {
    const exact = opts.find((o) => o.label.toLowerCase() === q || o.value.toLowerCase() === q || (o.display || "").toLowerCase() === q);
    if (exact) pick(exact);
    else if (!q && value) { setOpen(false); onChange(null); }
    else { setOpen(false); setText(shownText(current)); }
  };
  useEffect(() => { listRef.current?.children[hi]?.scrollIntoView({ block: "nearest" }); }, [hi]);

  return (
    <div className={cn("relative", className)}>
      <input
        data-testid={testid} value={text} placeholder={placeholder} autoComplete="off"
        onFocus={(e) => { setOpen(true); setHi(0); e.target.select(); }}
        onChange={(e) => { setText(e.target.value); setOpen(true); setHi(0); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" || e.key === "Tab") { if (open && filtered[hi]) { if (e.key === "Enter") e.preventDefault(); pick(filtered[hi]); } }
          else if (e.key === "Escape") { setOpen(false); setText(shownText(current)); }
        }}
        className={cn(inputCls, inputClassName)}
      />
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      {open && (
        <div ref={listRef} className="absolute z-50 mt-1 max-h-56 w-full min-w-[12rem] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg" data-testid={testid ? `${testid}-menu` : undefined}>
          {filtered.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No matches</div>}
          {filtered.map((o, i) => (
            <button key={o.value} type="button" onMouseDown={(e) => { e.preventDefault(); pick(o); }} onMouseEnter={() => setHi(i)}
              data-testid={testid ? `${testid}-option-${o.value.replace(/\s/g, "-").toLowerCase()}` : undefined}
              className={cn("block w-full truncate px-3 py-1.5 text-left text-sm text-slate-700", i === hi && "bg-sky-50 text-sky-800", o.value === value && "font-semibold")}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
